import { environment } from "./libs/xplat/core/src/lib/environments/environment";
import { Amplify } from "aws-amplify";

/**
 * Decode JWT token to get payload
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

/**
 * Detect which Cognito pool to use based on existing tokens in localStorage
 */
function detectCognitoPool(): { clientId: string; userPoolId: string; domain: string } | null {
  // Check for PreSonus tokens first
  const presonusPrefix = `CognitoIdentityServiceProvider.${environment.cognito.clientId}`;
  const presonusLastAuthUser = localStorage.getItem(`${presonusPrefix}.LastAuthUser`);

  if (presonusLastAuthUser) {
    const presonusIdToken = localStorage.getItem(`${presonusPrefix}.${presonusLastAuthUser}.idToken`);
    if (presonusIdToken) {
      const payload = decodeJWT(presonusIdToken);
      if (payload?.aud === environment.cognito.clientId) {
        if (!environment.production) {
          console.log("🔵 Detected PreSonus tokens - configuring for PreSonus pool");
        }
        return {
          clientId: environment.cognito.clientId,
          userPoolId: environment.cognito.userPoolId,
          domain: environment.cognito.domain,
        };
      }
    }
  }

  // Check for Fender tokens
  const fenderPrefix = `CognitoIdentityServiceProvider.${environment.fenderCognito.clientId}`;
  const fenderLastAuthUser = localStorage.getItem(`${fenderPrefix}.LastAuthUser`);

  if (fenderLastAuthUser) {
    const fenderIdToken = localStorage.getItem(`${fenderPrefix}.${fenderLastAuthUser}.idToken`);
    if (fenderIdToken) {
      const payload = decodeJWT(fenderIdToken);
      if (payload?.aud === environment.fenderCognito.clientId) {
        if (!environment.production) {
          console.log("🟠 Detected Fender tokens - configuring for Fender pool");
        }
        return {
          clientId: environment.fenderCognito.clientId,
          userPoolId: environment.fenderCognito.userPoolId,
          domain: environment.fenderCognito.domain,
        };
      }
    }
  }

  return null; // No tokens found
}

export function configureCognito(signInUrl: string, signOutUrl: string) {
  // Try to detect pool from existing tokens first
  const detectedPool = detectCognitoPool();

  let clientId: string;
  let userPoolId: string;
  let domain: string;

  if (detectedPool) {
    // Use detected pool from tokens
    clientId = detectedPool.clientId;
    userPoolId = detectedPool.userPoolId;
    domain = detectedPool.domain;
  } else {
    // No tokens found, use default Fender pool
    clientId = environment.fenderCognito.clientId;
    userPoolId = environment.fenderCognito.userPoolId;
    domain = environment.fenderCognito.domain;

    // Check for SSO params or client_id overrides (only when no tokens exist)
    let ssoParamsString = localStorage.getItem("myp.sso_params");
    if (ssoParamsString) {
      let ssoParams = JSON.parse(ssoParamsString);
      if (ssoParams.client_id && environment.fenderCognito.appClients[ssoParams.client_id]) {
        clientId = environment.fenderCognito.appClients[ssoParams.client_id];
      }
    }

    let searchParams = new URLSearchParams(window.location.search);
    let searchParamClientId = searchParams.get("client_id");
    if (searchParamClientId && environment.fenderCognito.appClients[searchParamClientId]) {
      clientId = environment.fenderCognito.appClients[searchParamClientId];
    }

    if (!environment.production) {
      console.log("🆕 No tokens found - configuring for default Fender pool");
    }
  }
  let config: any = {
    userPoolClientId: clientId,
    userPoolId: userPoolId,
    loginWith: {
      oauth: {
        providers: ["Google", "Facebook", "Apple"],
        domain: domain,
        scopes: ["email", "profile", "openid", "aws.cognito.signin.user.admin"],
        redirectSignIn: [signInUrl],
        redirectSignOut: [signOutUrl],
        responseType: "code",
      },
    },
  };
  Amplify.configure({
    Auth: {
      Cognito: config,
    },
  });

  // Let Cognito use default localStorage for tokens
  // Tokens will be manually synced to cookies for cross-domain access via app.service.ts
}
